import { awardXp, XpActionType } from '@/lib/gamification';
import { supabase } from '@/lib/supabase';
import { decode } from 'base64-arraybuffer';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useState } from 'react';
import { Platform } from 'react-native';

// ✅ Import conditionnel : expo-file-system uniquement en natif
let FileSystem: typeof import('expo-file-system/legacy') | null = null;
if (Platform.OS !== 'web') {
  FileSystem = require('expo-file-system/legacy');
}

// ═══════════════════════════════════════════════════════════════
// ✅ HELPERS CROSS-PLATFORM (web + natif) — NE CHANGE RIEN AU NATIF
// ═══════════════════════════════════════════════════════════════

async function getFileInfo(uri: string): Promise<{ exists: boolean; size?: number }> {
  if (Platform.OS !== 'web' && FileSystem) {
    return FileSystem.getInfoAsync(uri);
  }
  try {
    const response = await fetch(uri);
    const blob = await response.blob();
    return { exists: true, size: blob.size };
  } catch (error) {
    console.error('[WEB] getFileInfo error:', error);
    return { exists: false };
  }
}

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS !== 'web' && FileSystem) {
    return FileSystem.readAsStringAsync(uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
  }
  const response = await fetch(uri);
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      } else {
        reject(new Error('FileReader result is not a string'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * ✅ Upload cross-platform optimisé
 * - Web : upload le Blob directement (pas de conversion base64 → BEAUCOUP plus rapide)
 * - Natif : passe par base64 → arrayBuffer (comme avant, aucun changement)
 */
async function uploadToStorage(
  fileUri: string,
  storagePath: string,
  mimeType: string
): Promise<{ error: any }> {
  if (Platform.OS === 'web') {
    // ✅ Web : fetch → blob → upload direct (skip base64 entièrement)
    console.log('[UPLOAD-WEB] Using optimized blob upload');
    const response = await fetch(fileUri);
    const blob = await response.blob();
    return supabase.storage
      .from('courses')
      .upload(storagePath, blob, {
        contentType: mimeType,
        upsert: false,
      });
  } else {
    // ✅ Natif : base64 → arrayBuffer (inchangé)
    const base64Data = await readFileAsBase64(fileUri);
    const arrayBuffer = decode(base64Data);
    return supabase.storage
      .from('courses')
      .upload(storagePath, arrayBuffer, {
        contentType: mimeType,
        upsert: false,
      });
  }
}

// ═══════════════════════════════════════════════════════════════
// ✅ FIX: Helper pour appeler generate-from-document avec timeout
// et gestion propre des erreurs HTML (504 Dify etc.)
// ═══════════════════════════════════════════════════════════════

const GENERATE_TIMEOUT_MS = 120000; // 2 minutes max

async function callGenerateWithTimeout(
  documentId: string,
  intent: string,
  accessToken: string
): Promise<{ success: boolean; data?: any; error?: string; errorCode?: string }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);

  try {
    const response = await fetch(
      `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/generate-from-document`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          document_id: documentId,
          intent: intent,
        }),
        signal: controller.signal,
      }
    );
    clearTimeout(timeoutId);

    const responseText = await response.text();
    console.log(`[GENERATE] Raw response: ${responseText.substring(0, 500)}`);

    // ✅ FIX: Détecter les réponses HTML (erreur 504 Dify, timeout serveur, etc.)
    if (responseText.trimStart().startsWith('<!DOCTYPE') || responseText.trimStart().startsWith('<html')) {
      console.error('[GENERATE] Received HTML error page instead of JSON');
      return {
        success: false,
        error: 'Le serveur met trop de temps à répondre. Essaie avec un document plus court (1 à 2 pages max).',
      };
    }

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('[GENERATE] Failed to parse JSON:', responseText.substring(0, 200));
      return {
        success: false,
        error: 'Erreur de communication avec le serveur. Réessaie dans quelques instants.',
      };
    }

    if (!response.ok || result.error) {
      console.error('[GENERATE] Error:', result);
      
      // ✅ FIX: Messages d'erreur lisibles selon le type
      let userMessage = result.error || 'Erreur lors de la génération';
      if (typeof userMessage === 'string' && userMessage.includes('DIFY_WORKFLOW_RUN_FAILED')) {
        userMessage = 'La génération a échoué. Essaie avec un document plus court ou une photo plus nette.';
      }
      
      return {
        success: false,
        error: userMessage,
        errorCode: result.code || undefined,
      };
    }

    return { success: true, data: result };

  } catch (fetchError: any) {
    clearTimeout(timeoutId);
    
    if (fetchError.name === 'AbortError') {
      console.error('[GENERATE] Timeout after', GENERATE_TIMEOUT_MS, 'ms');
      return {
        success: false,
        error: 'La génération prend trop de temps. Essaie avec un document plus court (1 à 2 pages max) ou une photo plus nette.',
      };
    }

    if (fetchError.message === 'Network request failed') {
      return {
        success: false,
        error: 'Problème de connexion. Vérifie ton internet et réessaie.',
      };
    }

    console.error('[GENERATE] Fetch error:', fetchError);
    return {
      success: false,
      error: 'Erreur inattendue. Réessaie dans quelques instants.',
    };
  }
}

// ═══════════════════════════════════════════════════════════════

type ContentIntent = 'fiche' | 'quiz' | 'flashcards';

export const FREE_LIMITS = {
  fiches: 5,
  quiz: 5,
  flashcards: 5,
  coach: 10,
};

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UploadResult {
  success: boolean;
  noteId?: string;
  quizId?: string;
  flashcardSetId?: string;
  error?: string;
  errorCode?: string;
  xpAwarded?: number;
  newStreak?: number;
  leveledUp?: boolean;
  feedbackMessage?: string;
}

interface UploadState {
  isUploading: boolean;
  isProcessing: boolean;
  isGenerating: boolean;
  progress: string;
  error: string | null;
}

export interface PhotoItem {
  id: string;
  uri: string;
  fileName: string;
}

const getMonthStart = (): string => {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  monthStart.setUTCHours(0, 0, 0, 0);
  return monthStart.toISOString();
};

export function useDocumentUpload() {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    isProcessing: false,
    isGenerating: false,
    progress: '',
    error: null,
  });

  const [photos, setPhotos] = useState<PhotoItem[]>([]);

  const resetState = () => {
    setState({
      isUploading: false,
      isProcessing: false,
      isGenerating: false,
      progress: '',
      error: null,
    });
    setPhotos([]);
  };

  const addPhoto = (uri: string, fileName: string) => {
    const newPhoto: PhotoItem = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      uri,
      fileName,
    };
    setPhotos(prev => [...prev, newPhoto]);
    return newPhoto;
  };

  const removePhoto = (id: string) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };

  const clearPhotos = () => {
    setPhotos([]);
  };

  const pickImage = async (useCamera: boolean = false): Promise<string | null> => {
    try {
      if (useCamera) {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) {
          setState(prev => ({ ...prev, error: 'Permission caméra refusée' }));
          return null;
        }
        
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: ['images'],
          quality: 0.7,
        });
        
        if (!result.canceled && result.assets[0]) {
          return result.assets[0].uri;
        }
      } else {
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          quality: 0.7,
          allowsMultipleSelection: true,
        });
        
        if (!result.canceled && result.assets.length > 0) {
          result.assets.forEach((asset, index) => {
            addPhoto(asset.uri, `image_${Date.now()}_${index}.jpg`);
          });
          return result.assets[0].uri;
        }
      }
      return null;
    } catch (error) {
      console.error('Error picking image:', error);
      setState(prev => ({ ...prev, error: 'Erreur lors de la sélection de l\'image' }));
      return null;
    }
  };

  const pickDocument = async (): Promise<{ uri: string | null; error?: string }> => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        const file = result.assets[0];
        
        if (file.size && file.size > MAX_FILE_SIZE_BYTES) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
          return { uri: null, error: `FILE_TOO_LARGE:${fileSizeMB}` };
        }
        
        return { uri: file.uri };
      }
      return { uri: null };
    } catch (error) {
      console.error('Error picking document:', error);
      return { uri: null, error: 'Erreur lors de la sélection du document' };
    }
  };

  const checkFreeLimit = async (
    userId: string, 
    intent: ContentIntent
  ): Promise<{ allowed: boolean; message?: string; currentCount?: number }> => {
    try {
      console.log('[LIMIT CHECK] Checking monthly limit for user:', userId, 'intent:', intent);

      const { data: profile } = await supabase
        .from('profiles')
        .select('premium')
        .eq('id', userId)
        .single();

      if (profile?.premium) {
        console.log('[LIMIT CHECK] User is Premium - unlimited access');
        return { allowed: true };
      }

      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('status')
        .eq('user_id', userId)
        .maybeSingle();

      const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

      if (isActive) {
        console.log('[LIMIT CHECK] User has active subscription');
        return { allowed: true };
      }

      const monthStart = getMonthStart();

      let tableName: string;
      let limit: number;
      let contentName: string;

      switch (intent) {
        case 'fiche':
          tableName = 'notes';
          limit = FREE_LIMITS.fiches;
          contentName = 'fiche';
          break;
        case 'quiz':
          tableName = 'quizzes';
          limit = FREE_LIMITS.quiz;
          contentName = 'quiz';
          break;
        case 'flashcards':
          tableName = 'flashcard_sets';
          limit = FREE_LIMITS.flashcards;
          contentName = 'set de flashcards';
          break;
        default:
          return { allowed: true };
      }

      const { count, error } = await supabase
        .from(tableName)
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', monthStart);

      if (error) {
        console.error('[LIMIT CHECK] Error:', error);
        return { allowed: true };
      }

      const currentCount = count ?? 0;
      console.log(`[LIMIT CHECK] ${tableName} this month: ${currentCount}/${limit}`);

      if (currentCount >= limit) {
        return { 
          allowed: false,
          currentCount,
          message: `Tu as atteint ta limite de ${limit} ${contentName}${limit > 1 ? 's' : ''} par mois. Passe Premium pour un accès illimité ! 🚀` 
        };
      }

      return { allowed: true, currentCount };
    } catch (error) {
      console.error('[LIMIT CHECK] Exception:', error);
      return { allowed: true };
    }
  };

  const uploadSingleFile = async (
    fileUri: string,
    fileName: string,
    userId: string,
    session: any
  ): Promise<{ documentId: string; extractedText: string } | null> => {
    try {
      let fileExt = fileUri.split('.').pop()?.toLowerCase() || 'jpg';
      if (!['jpg', 'jpeg', 'png', 'webp', 'pdf'].includes(fileExt)) {
        fileExt = 'jpg';
      }

      const isImage = ['jpg', 'jpeg', 'png', 'webp'].includes(fileExt);
      const mimeType = isImage 
        ? `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`
        : 'application/pdf';

      const finalFileName = fileName.includes('.') ? fileName : `${fileName}.${fileExt}`;
      const uniqueName = `${userId}/${Date.now()}_${Math.random().toString(36).substr(2, 5)}_${finalFileName}`;

      console.log(`[UPLOAD] Starting: ${uniqueName}`);

      const fileInfo = await getFileInfo(fileUri);
      if (!fileInfo.exists) {
        console.error('[UPLOAD] File not found:', fileUri);
        return null;
      }

      // ✅ Upload optimisé cross-platform
      const { error: uploadError } = await uploadToStorage(fileUri, uniqueName, mimeType);

      if (uploadError) {
        console.error('[UPLOAD] Error:', uploadError);
        return null;
      }

      console.log(`[UPLOAD] Success: ${uniqueName}`);

      const { data: document, error: insertError } = await supabase
        .from('documents')
        .insert({
          user_id: userId,
          original_filename: finalFileName,
          storage_path: uniqueName,
          title: finalFileName.replace(/\.[^/.]+$/, '') || 'Document uploadé',
          status: 'uploaded',
        })
        .select('id')
        .single();

      if (insertError || !document) {
        console.error('[DOCUMENT] Insert error:', insertError);
        return null;
      }

      const documentId = document.id;
      console.log(`[DOCUMENT] Created: ${documentId}`);

      const processResponse = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/process-document`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ document_id: documentId }),
        }
      );

      const processText = await processResponse.text();

      let processResult;
      try {
        processResult = JSON.parse(processText);
      } catch {
        console.error('[PROCESS] Failed to parse JSON');
        return null;
      }

      if (!processResponse.ok || processResult.error) {
        console.error('[PROCESS] Error:', processResult);
        return null;
      }

      console.log(`[PROCESS] Success: ${processResult.extracted_chars} chars`);

      const { data: docWithText } = await supabase
        .from('documents')
        .select('extracted_text')
        .eq('id', documentId)
        .single();

      return {
        documentId,
        extractedText: docWithText?.extracted_text || '',
      };
    } catch (error) {
      console.error('[UPLOAD] Exception:', error);
      return null;
    }
  };

  const uploadAndGenerate = async (
    fileUri: string,
    intent: ContentIntent,
    fileName?: string
  ): Promise<UploadResult> => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'Non authentifié' };
      }

      setState(prev => ({ ...prev, isUploading: true, progress: 'Vérification...' }));
      
      const limitCheck = await checkFreeLimit(user.id, intent);
      if (!limitCheck.allowed) {
        setState(prev => ({ ...prev, isUploading: false }));
        return { 
          success: false, 
          error: limitCheck.message || 'Limite atteinte',
          errorCode: 'FREE_LIMIT_REACHED',
        };
      }

      const fileInfo = await getFileInfo(fileUri);
      if (!fileInfo.exists) {
        return { success: false, error: 'Fichier introuvable' };
      }

      if (fileInfo.size && fileInfo.size > MAX_FILE_SIZE_BYTES) {
        const fileSizeMB = (fileInfo.size / (1024 * 1024)).toFixed(1);
        setState(prev => ({ ...prev, isUploading: false }));
        return { 
          success: false, 
          error: `FILE_TOO_LARGE:${fileSizeMB}` 
        };
      }

      setState(prev => ({ ...prev, progress: 'Upload du fichier...' }));

      let fileExt = fileUri.split('.').pop()?.toLowerCase() || 'jpg';
      if (!['jpg', 'jpeg', 'png', 'webp', 'pdf'].includes(fileExt)) {
        fileExt = 'jpg';
      }

      const isImage = ['jpg', 'jpeg', 'png', 'webp'].includes(fileExt);
      const mimeType = isImage 
        ? `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`
        : 'application/pdf';

      const safeFileName = fileName || `document.${fileExt}`;
      const finalFileName = safeFileName.includes('.') ? safeFileName : `${safeFileName}.${fileExt}`;
      const uniqueName = `${user.id}/${Date.now()}_${finalFileName}`;

      console.log(`[UPLOAD] Extension: ${fileExt}, MIME: ${mimeType}, Path: ${uniqueName}`);
      console.log(`[UPLOAD] File size: ${fileInfo.size} bytes`);

      // ✅ Upload optimisé cross-platform
      const { error: uploadError } = await uploadToStorage(fileUri, uniqueName, mimeType);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return { success: false, error: `Erreur upload: ${uploadError.message}` };
      }

      console.log(`[UPLOAD] Success: ${uniqueName}`);

      setState(prev => ({ ...prev, progress: 'Création du document...' }));

      const { data: document, error: insertError } = await supabase
        .from('documents')
        .insert({
          user_id: user.id,
          original_filename: finalFileName,
          storage_path: uniqueName,
          title: finalFileName.replace(/\.[^/.]+$/, '') || 'Document uploadé',
          status: 'uploaded',
        })
        .select('id')
        .single();

      if (insertError || !document) {
        console.error('Insert error:', insertError);
        return { success: false, error: `Erreur création document: ${insertError?.message}` };
      }

      const documentId = document.id;
      console.log(`[DOCUMENT] Created with ID: ${documentId}`);

      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        isProcessing: true, 
        progress: 'Extraction du texte...' 
      }));

      const { data: { session } } = await supabase.auth.getSession();
      
      console.log(`[PROCESS] Calling process-document for: ${documentId}`);

      const processResponse = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/process-document`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ document_id: documentId }),
        }
      );

      const processText = await processResponse.text();
      console.log(`[PROCESS] Raw response: ${processText.substring(0, 500)}`);

      let processResult;
      try {
        processResult = JSON.parse(processText);
      } catch (parseError) {
        console.error('[PROCESS] Failed to parse JSON:', processText.substring(0, 200));
        return {
          success: false,
          error: 'Erreur lors de l\'extraction du texte. Réessaie avec une photo plus nette.',
        };
      }

      if (!processResponse.ok || processResult.error) {
        console.error('Process error:', processResult);
        return { 
          success: false, 
          error: processResult.details || processResult.error || 'Erreur extraction texte' 
        };
      }

      console.log(`[PROCESS] Success: ${processResult.extracted_chars} chars extracted`);

      setState(prev => ({ 
        ...prev, 
        isProcessing: false, 
        isGenerating: true, 
        progress: `Génération ${intent === 'fiche' ? 'de la fiche' : intent === 'quiz' ? 'du quiz' : 'des flashcards'}... (peut prendre jusqu'à 2 min)` 
      }));

      console.log(`[GENERATE] Calling generate-from-document for: ${documentId}, intent: ${intent}`);

      // ✅ FIX: Utilise le helper avec timeout + gestion erreurs HTML
      const generateResult = await callGenerateWithTimeout(
        documentId,
        intent,
        session?.access_token || ''
      );

      if (!generateResult.success) {
        return {
          success: false,
          error: generateResult.error,
          errorCode: generateResult.errorCode,
        };
      }

      console.log(`[GENERATE] Success:`, generateResult.data);

      setState(prev => ({ ...prev, progress: 'Attribution des XP...' }));

      const xpActionType: XpActionType = 
        intent === 'fiche' ? 'note_created' :
        intent === 'quiz' ? 'quiz_completed' :
        'flashcards_created';

      const xpResult = await awardXp(user.id, xpActionType);

      if (xpResult.success) {
        console.log(`🎮 Gamification: +${xpResult.xpAwarded} XP | Streak: ${xpResult.newStreak} | ${xpResult.feedbackMessage}`);
      } else {
        console.warn(`⚠️ Gamification error: ${xpResult.error}`);
      }

      setState(prev => ({ ...prev, isGenerating: false, progress: 'Terminé !' }));

      return {
        success: true,
        noteId: generateResult.data.note_id,
        quizId: generateResult.data.quiz_id,
        flashcardSetId: generateResult.data.flashcard_set_id,
        xpAwarded: xpResult.xpAwarded,
        newStreak: xpResult.newStreak,
        leveledUp: xpResult.leveledUp,
        feedbackMessage: xpResult.feedbackMessage,
      };

    } catch (error) {
      console.error('Upload and generate error:', error);
      return { 
        success: false, 
        error: 'Une erreur est survenue. Réessaie avec un document plus court ou une photo plus nette.' 
      };
    } finally {
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        isProcessing: false, 
        isGenerating: false 
      }));
    }
  };

  const uploadMultipleAndGenerate = async (
    photoItems: PhotoItem[],
    intent: ContentIntent
  ): Promise<UploadResult> => {
    try {
      if (photoItems.length === 0) {
        return { success: false, error: 'Aucune photo sélectionnée' };
      }

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'Non authentifié' };
      }

      setState(prev => ({ ...prev, isUploading: true, progress: 'Vérification...' }));
      
      const limitCheck = await checkFreeLimit(user.id, intent);
      if (!limitCheck.allowed) {
        setState(prev => ({ ...prev, isUploading: false }));
        return { 
          success: false, 
          error: limitCheck.message || 'Limite atteinte',
          errorCode: 'FREE_LIMIT_REACHED',
        };
      }

      const { data: { session } } = await supabase.auth.getSession();

      setState(prev => ({ 
        ...prev, 
        progress: `Traitement de ${photoItems.length} photo${photoItems.length > 1 ? 's' : ''} en parallèle...` 
      }));

      console.log(`[MULTI-UPLOAD] Starting PARALLEL upload of ${photoItems.length} photos`);
      const startTime = Date.now();

      const uploadPromises = photoItems.map((photo, index) => 
        uploadSingleFile(photo.uri, photo.fileName, user.id, session)
          .then(result => {
            console.log(`[MULTI-UPLOAD] Photo ${index + 1}/${photoItems.length} done`);
            return result;
          })
          .catch(err => {
            console.error(`[MULTI-UPLOAD] Photo ${index + 1} failed:`, err);
            return null;
          })
      );

      const results = await Promise.all(uploadPromises);
      const successfulResults = results.filter((r): r is { documentId: string; extractedText: string } => r !== null);

      const uploadTime = Date.now() - startTime;
      console.log(`[MULTI-UPLOAD] Parallel upload completed in ${uploadTime}ms - ${successfulResults.length}/${photoItems.length} successful`);

      if (successfulResults.length === 0) {
        return { success: false, error: 'Aucune photo n\'a pu être traitée' };
      }

      setState(prev => ({ ...prev, progress: 'Fusion des pages...' }));
      
      const combinedText = successfulResults.map(r => r.extractedText).join('\n\n--- Page suivante ---\n\n');
      console.log(`[MULTI-UPLOAD] Combined text length: ${combinedText.length} chars`);

      const mainDocumentId = successfulResults[0].documentId;

      const { error: updateError } = await supabase
        .from('documents')
        .update({ 
          extracted_text: combinedText,
          title: `Cours multi-pages (${successfulResults.length} pages)`,
        })
        .eq('id', mainDocumentId);

      if (updateError) {
        console.error('[MULTI-UPLOAD] Failed to update combined text:', updateError);
      }

      setState(prev => ({ 
        ...prev, 
        isUploading: false,
        isProcessing: false, 
        isGenerating: true, 
        progress: `Génération ${intent === 'fiche' ? 'de la fiche' : intent === 'quiz' ? 'du quiz' : 'des flashcards'}... (peut prendre jusqu'à 2 min)` 
      }));

      console.log(`[GENERATE] Calling generate-from-document for combined: ${mainDocumentId}, intent: ${intent}`);

      // ✅ FIX: Utilise le helper avec timeout + gestion erreurs HTML
      const generateResult = await callGenerateWithTimeout(
        mainDocumentId,
        intent,
        session?.access_token || ''
      );

      if (!generateResult.success) {
        return {
          success: false,
          error: generateResult.error,
          errorCode: generateResult.errorCode,
        };
      }

      const totalTime = Date.now() - startTime;
      console.log(`[GENERATE] Success! Total time: ${totalTime}ms`);

      setState(prev => ({ ...prev, progress: 'Attribution des XP...' }));

      const xpActionType: XpActionType = 
        intent === 'fiche' ? 'note_created' :
        intent === 'quiz' ? 'quiz_completed' :
        'flashcards_created';

      const xpResult = await awardXp(user.id, xpActionType);

      if (xpResult.success) {
        console.log(`🎮 Gamification: +${xpResult.xpAwarded} XP | Streak: ${xpResult.newStreak} | ${xpResult.feedbackMessage}`);
      }

      setState(prev => ({ ...prev, isGenerating: false, progress: 'Terminé !' }));

      setPhotos([]);

      return {
        success: true,
        noteId: generateResult.data.note_id,
        quizId: generateResult.data.quiz_id,
        flashcardSetId: generateResult.data.flashcard_set_id,
        xpAwarded: xpResult.xpAwarded,
        newStreak: xpResult.newStreak,
        leveledUp: xpResult.leveledUp,
        feedbackMessage: xpResult.feedbackMessage,
      };

    } catch (error) {
      console.error('Multi-upload error:', error);
      return { 
        success: false, 
        error: 'Une erreur est survenue. Réessaie avec moins de photos ou des photos plus nettes.' 
      };
    } finally {
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        isProcessing: false, 
        isGenerating: false 
      }));
    }
  };

  const isLoading = state.isUploading || state.isProcessing || state.isGenerating;

  return {
    ...state,
    isLoading,
    photos,
    addPhoto,
    removePhoto,
    clearPhotos,
    pickImage,
    pickDocument,
    uploadAndGenerate,
    uploadMultipleAndGenerate,
    resetState,
  };
}